import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import {
  PageTitle, Badge, Button, Card,
  Table, THead, TBody, TR, TH, Row, Cell, EmptyState, Icons,
  Drawer, DrawerBody,
  Skeleton, SkeletonRows, SkeletonCard,
  CurrencyInput,
} from '../shared/ui';
import { VND } from '../shared/utils/format';

const STATUS_TONE = { open: 'warning', in_progress: 'info', resolved: 'success', closed: 'neutral' };
const PRIORITY_TONE = { low: 'success', medium: 'warning', high: 'danger', urgent: 'danger' };
const PAYMENT_TONE = {
  payment_required:  'danger',
  payment_pending:   'warning',
  payment_processing:'info',
  payment_completed: 'success',
};
const VERIFY_TONE = { verified: 'success', rejected: 'danger', pending: 'warning' };

const SELECT_CLS = 'h-9 px-3 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600';
const INPUT_CLS  = 'w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600';
const LABEL_CLS  = 'block text-xs font-medium text-stone-600 mb-1.5';

const TicketsUnified = () => {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation(['tickets', 'common']);
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';
  const locale = lang === 'en' ? 'en-US' : 'vi-VN';
  const { user } = useAuth();
  const { notifications } = useNotifications();
  const isManager = user?.role === 'admin' || user?.role === 'manager';
  const isRenter  = user?.role === 'renter';

  const [selectedTicket, setSelectedTicket]           = useState(null);
  const [showDetailModal, setShowDetailModal]         = useState(false);
  const [showPaymentModal, setShowPaymentModal]       = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [newComment, setNewComment]                   = useState('');
  const [serviceCharge, setServiceCharge]             = useState('');
  const [statusFilter, setStatusFilter]               = useState('all');
  const [priorityFilter, setPriorityFilter]           = useState('all');
  const [selectedPayment, setSelectedPayment]         = useState(null);

  const [paymentData, setPaymentData] = useState({ payment_method: 'bank_transfer', transaction_reference: '', notes: '' });
  const [verificationData, setVerificationData] = useState({ status: 'verified', notes: '', rejection_reason: '' });

  const { data: tickets = [], isLoading, refetch: refetchTickets } = useQuery({
    queryKey: ['tickets', statusFilter, priorityFilter, user?.id],
    queryFn: async () => {
      let url = '/api/tickets?';
      if (statusFilter   !== 'all') url += `status=${statusFilter}&`;
      if (priorityFilter !== 'all') url += `priority=${priorityFilter}`;
      const response = await api.get(url);
      const data = response.data;
      if (!Array.isArray(data)) return [];
      return data.filter(ticket => ticket && typeof ticket === 'object' && ticket.id);
    },
    select: (data) => Array.isArray(data) ? data : [],
  });

  const { data: ticketDetail, refetch: refetchDetail } = useQuery({
    queryKey: ['ticket-detail', selectedTicket?.id],
    queryFn: async () => {
      if (!selectedTicket || !selectedTicket.id) return null;
      try {
        const [ticketResponse, commentsResponse] = await Promise.all([
          api.get(`/api/tickets/${selectedTicket.id}`),
          api.get(`/api/tickets/${selectedTicket.id}/comments`).catch(() => ({ data: [] })),
        ]);
        const ticket = ticketResponse.data;
        if (!ticket || !ticket.id) return null;
        return { ticket, comments: Array.isArray(commentsResponse.data) ? commentsResponse.data : [] };
      } catch { return null; }
    },
    enabled: !!selectedTicket && !!selectedTicket.id,
  });

  const { data: payments = [], refetch: refetchPayments } = useQuery({
    queryKey: ['ticket-payments', selectedTicket?.id],
    queryFn: async () => {
      if (!selectedTicket) return [];
      try {
        const response = await api.get(`/api/tickets/${selectedTicket.id}/payments`);
        return response.data;
      } catch (error) {
        if (error.response?.status === 403) return [];
        throw error;
      }
    },
    enabled: !!selectedTicket && selectedTicket.service_charge > 0,
    select: (data) => Array.isArray(data) ? data : [],
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ ticketId, status }) => {
      const response = await api.put(`/api/tickets/${ticketId}/status`, { status });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-detail'] });
      toast.success(t('tickets:toast.statusUpdated'));
      refetchTickets(); refetchDetail();
    },
    onError: (error) => toast.error(error.response?.data?.detail || t('tickets:toast.statusUpdateFailed')),
  });

  const addCommentMutation = useMutation({
    mutationFn: async ({ ticketId, comment }) => {
      const response = await api.post(`/api/tickets/${ticketId}/comments`, { ticket_id: ticketId, comment });
      return response.data;
    },
    onSuccess: () => { setNewComment(''); refetchDetail(); toast.success(t('tickets:toast.commentAdded')); },
    onError: (error) => toast.error(error.response?.data?.detail || t('tickets:toast.commentAddFailed')),
  });

  const updateServiceChargeMutation = useMutation({
    mutationFn: async ({ ticketId, charge }) => {
      const response = await api.put(`/api/tickets/${ticketId}/service-charge`, { service_charge: parseFloat(charge) });
      return response.data;
    },
    onSuccess: () => { setServiceCharge(''); refetchDetail(); refetchTickets(); toast.success(t('tickets:toast.chargeUpdated')); },
    onError: (error) => toast.error(error.response?.data?.detail || t('tickets:toast.chargeUpdateFailed')),
  });

  const submitPaymentMutation = useMutation({
    mutationFn: async ({ ticketId, paymentData: pd }) => {
      const response = await api.post(`/api/tickets/${ticketId}/payment`, { ...pd, amount: ticketDetail.ticket.service_charge });
      return response.data;
    },
    onSuccess: () => {
      setShowPaymentModal(false);
      setPaymentData({ payment_method: 'bank_transfer', transaction_reference: '', notes: '' });
      refetchDetail(); refetchPayments(); refetchTickets();
      toast.success(t('tickets:toast.paymentSubmitted'));
    },
    onError: (error) => toast.error(error.response?.data?.detail || t('tickets:toast.paymentSubmitFailed')),
  });

  const verifyPaymentMutation = useMutation({
    mutationFn: async ({ paymentId, data }) => {
      const response = await api.put(`/api/tickets/payment/${paymentId}/verify`, data);
      return response.data;
    },
    onSuccess: () => {
      setShowVerificationModal(false); setSelectedPayment(null);
      setVerificationData({ status: 'verified', notes: '', rejection_reason: '' });
      refetchPayments(); refetchDetail(); refetchTickets();
      toast.success(t('tickets:toast.paymentVerified'));
    },
    onError: (error) => toast.error(error.response?.data?.detail || t('tickets:toast.paymentVerifyFailed')),
  });

  useEffect(() => {
    const relevant = notifications.filter(n => ['ticket_update', 'payment_update', 'ticket_comment'].includes(n.type));
    if (relevant.length > 0) {
      refetchTickets();
      if (selectedTicket) { refetchDetail(); refetchPayments(); }
    }
  }, [notifications]);

  const handleViewDetail = (ticket) => { setSelectedTicket(ticket); setShowDetailModal(true); };
  const handleAddComment = () => { if (newComment.trim()) addCommentMutation.mutate({ ticketId: selectedTicket.id, comment: newComment }); };
  const handleUpdateServiceCharge = () => { if (serviceCharge && !isNaN(serviceCharge)) updateServiceChargeMutation.mutate({ ticketId: selectedTicket.id, charge: serviceCharge }); };
  const handleSubmitPayment = () => {
    if (!paymentData.transaction_reference.trim()) { toast.error(t('tickets:toast.refRequired')); return; }
    submitPaymentMutation.mutate({ ticketId: selectedTicket.id, paymentData });
  };
  const handleVerifyPayment = () => {
    if (verificationData.status === 'rejected' && !verificationData.rejection_reason) { toast.error(t('tickets:toast.rejectionReasonRequired')); return; }
    verifyPaymentMutation.mutate({ paymentId: selectedPayment.id, data: verificationData });
  };

  // Look up status / priority / payment labels via i18n.
  const statusLabel   = (s) => t(`tickets:status.${s}`, { defaultValue: (s || '').replace('_', ' ') });
  const priorityLabel = (p) => t(`tickets:priority.${p}`, { defaultValue: p });
  const paymentLabel  = (p) => t(`tickets:payment.${p}`, { defaultValue: p });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-72 rounded-lg" />
        <div className="grid grid-cols-3 gap-4">
          <SkeletonCard /><SkeletonCard /><SkeletonCard />
        </div>
        <Card className="overflow-hidden">
          <Table>
            <THead>
              <TR>{['id','title','room','status','priority','charge','created','actions'].map(h => <TH key={h}><Skeleton width={h === 'title' ? 120 : 48} height={10} /></TH>)}</TR>
            </THead>
            <TBody><SkeletonRows count={8} columns={8} /></TBody>
          </Table>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title={isRenter ? t('tickets:title.renter') : t('tickets:title.manager')}
        subtitle={isRenter ? t('tickets:subtitle.renter') : t('tickets:subtitle.manager')}
        actions={
          <div className="flex gap-2">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={SELECT_CLS}>
              <option value="all">{t('tickets:filter.allStatus')}</option>
              <option value="open">{t('tickets:status.open')}</option>
              <option value="in_progress">{t('tickets:status.in_progress')}</option>
              <option value="resolved">{t('tickets:status.resolved')}</option>
              <option value="closed">{t('tickets:status.closed')}</option>
            </select>
            <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className={SELECT_CLS}>
              <option value="all">{t('tickets:filter.allPriority')}</option>
              <option value="low">{t('tickets:priority.low')}</option>
              <option value="medium">{t('tickets:priority.medium')}</option>
              <option value="high">{t('tickets:priority.high')}</option>
              <option value="urgent">{t('tickets:priority.urgent')}</option>
            </select>
          </div>
        }
      />

      {/* Tickets Table */}
      <Card>
        {!tickets.filter(tk => tk && tk.id).length ? (
          <div className="p-12">
            <EmptyState icon={<Icons.Ticket size={32} />} title={t('tickets:empty.title')} description={t('tickets:empty.description')} />
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>{t('tickets:col.id')}</TH>
                <TH>{t('tickets:col.title')}</TH>
                {isManager && <TH>{t('tickets:col.room')}</TH>}
                <TH>{t('tickets:col.status')}</TH>
                <TH>{t('tickets:col.priority')}</TH>
                <TH>{t('tickets:col.payment')}</TH>
                <TH>{t('tickets:col.charge')}</TH>
                <TH>{t('tickets:col.created')}</TH>
                <TH>{t('tickets:col.actions')}</TH>
              </TR>
            </THead>
            <TBody>
              {tickets.filter(tk => tk && tk.id).map((ticket) => (
                <Row key={ticket.id}>
                  <Cell><span className="text-sm font-semibold text-stone-900">#{ticket.id}</span></Cell>
                  <Cell><span className="text-sm font-medium text-stone-900">{ticket.title || ticket.subject}</span></Cell>
                  {isManager && (
                    <Cell><Badge tone="neutral">{ticket.room_number || ticket.room_id || 'N/A'}</Badge></Cell>
                  )}
                  <Cell>
                    <Badge tone={STATUS_TONE[ticket.status] || 'neutral'}>{statusLabel(ticket.status)}</Badge>
                  </Cell>
                  <Cell>
                    <Badge tone={PRIORITY_TONE[ticket.priority] || 'neutral'}>{priorityLabel(ticket.priority)}</Badge>
                  </Cell>
                  <Cell>
                    {ticket.payment_status && (
                      <Badge tone={PAYMENT_TONE[ticket.payment_status] || 'neutral'}>{paymentLabel(ticket.payment_status)}</Badge>
                    )}
                  </Cell>
                  <Cell>
                    <span className="text-sm font-bold text-stone-900">
                      {ticket.service_charge ? `$${parseFloat(ticket.service_charge).toLocaleString(locale)}` : '-'}
                    </span>
                  </Cell>
                  <Cell>
                    <div className="flex items-center gap-1.5 text-xs text-stone-500">
                      <Icons.Clock size={12} className="text-stone-400 shrink-0" />
                      {new Date(ticket.created_at).toLocaleDateString(locale)}
                    </div>
                  </Cell>
                  <Cell>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleViewDetail(ticket)} className="text-xs text-teal-600 hover:text-teal-700 font-semibold">
                        {t('tickets:action.viewDetails')}
                      </button>
                      {isManager && ticket.status === 'open' && (
                        <button
                          onClick={() => updateStatusMutation.mutate({ ticketId: ticket.id, status: 'in_progress' })}
                          className="text-xs text-green-600 hover:text-green-700 font-semibold"
                        >
                          {t('tickets:action.start')}
                        </button>
                      )}
                    </div>
                  </Cell>
                </Row>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {/* Ticket Detail Drawer */}
      <Drawer
        open={!!(showDetailModal && ticketDetail?.ticket)}
        onClose={() => { setShowDetailModal(false); setSelectedTicket(null); }}
        title={ticketDetail?.ticket ? `#${ticketDetail.ticket.id}` : ''}
        subtitle={ticketDetail?.ticket?.title || ticketDetail?.ticket?.subject}
        width={520}
      >
        <DrawerBody>
        {ticketDetail?.ticket && (
          <div className="p-5 space-y-6">
              {/* Ticket Info */}
              <div className="bg-stone-50 rounded-xl p-5 border border-stone-100">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  <div>
                    <p className="text-xs font-bold text-stone-400 uppercase tracking-wide mb-1">{t('tickets:detail.kv.title')}</p>
                    <p className="text-stone-900 font-semibold leading-tight">{ticketDetail.ticket.title || ticketDetail.ticket.subject}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-stone-400 uppercase tracking-wide mb-1">{t('tickets:detail.kv.status')}</p>
                    {isManager ? (
                      <select
                        value={ticketDetail.ticket.status}
                        onChange={(e) => updateStatusMutation.mutate({ ticketId: ticketDetail.ticket.id, status: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm rounded-lg border border-stone-200 focus:border-teal-600 focus:ring-1 focus:ring-teal-600 outline-none bg-white"
                      >
                        <option value="open">{t('tickets:status.open')}</option>
                        <option value="in_progress">{t('tickets:status.in_progress')}</option>
                        <option value="resolved">{t('tickets:status.resolved')}</option>
                        <option value="closed">{t('tickets:status.closed')}</option>
                      </select>
                    ) : (
                      <Badge tone={STATUS_TONE[ticketDetail.ticket.status] || 'neutral'}>{statusLabel(ticketDetail.ticket.status)}</Badge>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-stone-400 uppercase tracking-wide mb-1">{t('tickets:detail.kv.priority')}</p>
                    <Badge tone={PRIORITY_TONE[ticketDetail.ticket.priority] || 'neutral'}>{priorityLabel(ticketDetail.ticket.priority)}</Badge>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-stone-400 uppercase tracking-wide mb-1">{t('tickets:detail.kv.location')}</p>
                    <div className="flex items-center gap-2 text-sm text-stone-700 font-medium">
                      <Icons.Home size={14} className="text-stone-400 shrink-0" />
                      {ticketDetail.ticket.room_number || 'N/A'} · {ticketDetail.ticket.building_name || 'N/A'}
                    </div>
                  </div>
                  {isManager && (
                    <div>
                      <p className="text-xs font-bold text-stone-400 uppercase tracking-wide mb-1">{t('tickets:detail.kv.tenant')}</p>
                      <div className="flex items-center gap-2 text-sm text-stone-700 font-medium">
                        <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center text-xs font-bold text-teal-700">
                          {ticketDetail.ticket.renter_first_name?.[0]}
                        </div>
                        {ticketDetail.ticket.renter_first_name} {ticketDetail.ticket.renter_last_name}
                      </div>
                    </div>
                  )}
                  {ticketDetail.ticket.furniture_name && (
                    <div>
                      <p className="text-xs font-bold text-stone-400 uppercase tracking-wide mb-1">{t('tickets:detail.kv.asset')}</p>
                      <p className="text-sm text-stone-700 font-medium">
                        {ticketDetail.ticket.furniture_name}
                        {ticketDetail.ticket.furniture_years_of_use && (
                          <span className="text-stone-400 text-xs ml-1">
                            ({t('tickets:detail.assetYears', { years: ticketDetail.ticket.furniture_years_of_use })})
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
                <div className="mt-5 pt-5 border-t border-stone-100">
                  <p className="text-xs font-bold text-stone-400 uppercase tracking-wide mb-2">{t('tickets:detail.kv.description')}</p>
                  <p className="text-sm text-stone-600 leading-relaxed bg-white rounded-xl p-4 border border-stone-100">{ticketDetail.ticket.description}</p>
                </div>
              </div>

              {/* Service Charge Section */}
              <div className="bg-white rounded-xl border border-stone-100 p-5">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <Icons.Wallet size={16} className="text-teal-600" />
                      <p className="text-sm font-bold text-stone-900">{t('tickets:detail.section.serviceCharge.title')}</p>
                    </div>
                    <p className="text-xs text-stone-400">{t('tickets:detail.section.serviceCharge.subtitle')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-stone-400 uppercase font-bold tracking-wide">{t('tickets:detail.section.serviceCharge.totalCharge')}</p>
                    <p className="text-2xl font-bold text-teal-700">${ticketDetail.ticket.service_charge || '0.00'}</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 py-4 border-t border-b border-stone-50 mb-4">
                  {ticketDetail.ticket.payment_status && (
                    <Badge tone={PAYMENT_TONE[ticketDetail.ticket.payment_status] || 'neutral'}>{paymentLabel(ticketDetail.ticket.payment_status)}</Badge>
                  )}
                  {isManager && (
                    <div className="flex items-center gap-2 ml-auto">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm font-medium">$</span>
                        <CurrencyInput value={serviceCharge} onChange={(e) => setServiceCharge(e.target.value)} placeholder="0" className="pl-7 pr-3 py-1.5 w-28 text-sm border border-stone-200 rounded-lg focus:border-teal-600 focus:ring-1 focus:ring-teal-600 outline-none font-bold" />
                      </div>
                      <Button kind="secondary" size="sm" onClick={handleUpdateServiceCharge}>{t('tickets:action.update')}</Button>
                    </div>
                  )}
                  {isRenter && ticketDetail.ticket.payment_status === 'payment_required' && (
                    <Button kind="primary" iconLeft={<Icons.CreditCard size={14} />} className="ml-auto" onClick={() => setShowPaymentModal(true)}>
                      {t('tickets:action.makePayment')}
                    </Button>
                  )}
                </div>

                {/* Payment History */}
                {payments.length > 0 && (
                  <div className="bg-stone-50 rounded-xl p-4 border border-stone-100">
                    <p className="text-xs font-bold text-stone-400 uppercase tracking-wide mb-3">{t('tickets:detail.section.payments.history')}</p>
                    <div className="space-y-3">
                      {payments.filter(p => p && p.id).map((payment) => (
                        <div key={payment.id} className="bg-white rounded-xl p-4 border border-stone-100">
                          <div className="flex items-start justify-between">
                            <div className="flex gap-3">
                              <div className="p-2 bg-teal-50 rounded-lg shrink-0">
                                <Icons.Wallet size={16} className="text-teal-600" />
                              </div>
                              <div>
                                <p className="text-sm font-bold text-stone-900">
                                  ${payment.amount} <span className="text-stone-300 mx-1">·</span>
                                  <span className="font-medium text-stone-600 capitalize">{payment.payment_method.replace('_', ' ')}</span>
                                </p>
                                <code className="text-xs text-stone-400 bg-stone-50 px-1.5 py-0.5 rounded border border-stone-200">#{payment.transaction_reference || 'N/A'}</code>
                                {payment.notes && <p className="text-xs text-stone-400 mt-1.5 italic">"{payment.notes}"</p>}
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge tone={VERIFY_TONE[payment.status] || 'neutral'}>{payment.status.toUpperCase()}</Badge>
                              <p className="text-xs text-stone-400 mt-1">{new Date(payment.submitted_at).toLocaleString(locale)}</p>
                              {isManager && payment.status === 'pending' && (
                                <button onClick={() => { setSelectedPayment(payment); setShowVerificationModal(true); }} className="mt-1.5 text-xs font-bold text-teal-600 hover:text-teal-800">
                                  {t('tickets:action.verifyNow')}
                                </button>
                              )}
                            </div>
                          </div>
                          {payment.rejection_reason && (
                            <div className="mt-3 bg-red-50 border border-red-100 rounded-lg p-3 flex gap-2 text-xs text-red-800">
                              <Icons.AlertCircle size={14} className="shrink-0 mt-0.5" />
                              <div><strong>{t('tickets:detail.section.payments.declined')}</strong> {payment.rejection_reason}</div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Comments Section */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Icons.Send size={16} className="text-teal-600" />
                  <p className="text-base font-bold text-stone-900">{t('tickets:detail.section.discussion.title')}</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-5 border border-stone-100 min-h-[300px] flex flex-col">
                  <div className="mb-5 bg-white p-4 rounded-xl border border-stone-100">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder={t('tickets:detail.section.discussion.placeholder')}
                      className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:border-teal-600 focus:ring-1 focus:ring-teal-600 outline-none resize-none bg-stone-50"
                      rows="3"
                    />
                    <div className="flex justify-end mt-3">
                      <Button kind="primary" iconLeft={<Icons.Send size={14} />} onClick={handleAddComment} loading={addCommentMutation.isPending}>
                        {t('tickets:detail.section.discussion.send')}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-5 flex-grow">
                    {ticketDetail.comments && ticketDetail.comments.length > 0 ? (
                      ticketDetail.comments.filter(c => c && c.id).map((comment) => (
                        <div key={comment.id} className="flex gap-4">
                          <div className="shrink-0">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shadow-sm ${['manager', 'admin'].includes(comment.role) ? 'bg-teal-600 text-white' : 'bg-white border border-stone-200 text-stone-700'}`}>
                              {comment.first_name?.[0] || 'U'}
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-bold text-stone-900">
                                {comment.first_name && comment.last_name
                                  ? `${comment.first_name} ${comment.last_name}`
                                  : t('tickets:detail.tenantFallback', { id: comment.user_id })}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-bold uppercase tracking-wide ${['manager', 'admin'].includes(comment.role) ? 'bg-teal-50 text-teal-600' : 'bg-stone-100 text-stone-500'}`}>
                                {comment.role || 'User'}
                              </span>
                              <span className="text-xs text-stone-400 ml-auto">{new Date(comment.created_at).toLocaleString(locale)}</span>
                            </div>
                            <div className="bg-white p-3 rounded-xl border border-stone-100 text-sm text-stone-700 leading-relaxed">{comment.comment}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="flex flex-col items-center justify-center h-24 text-stone-400 gap-2">
                        <Icons.Send size={24} className="opacity-30" />
                        <p className="text-sm">{t('tickets:detail.section.discussion.empty')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
          </div>
        )}
        </DrawerBody>
      </Drawer>

      {/* Payment Modal (Renters) */}
      {showPaymentModal && isRenter && (
        <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
            <div className="px-6 py-4 border-b border-stone-100 flex items-center gap-2">
              <Icons.CreditCard size={18} className="text-teal-600" />
              <h2 className="text-lg font-bold text-stone-900">{t('tickets:paymentModal.title')}</h2>
            </div>
            <div className="p-6 space-y-5">
              <div className="bg-teal-50 rounded-xl p-5 text-center border border-teal-100">
                <p className="text-xs font-semibold text-teal-600 uppercase tracking-wide mb-1">{t('tickets:paymentModal.amountDue')}</p>
                <p className="text-4xl font-bold text-teal-900">{VND(ticketDetail?.ticket?.service_charge)}</p>
              </div>
              <div>
                <label className={LABEL_CLS}>{t('tickets:paymentModal.label.method')}</label>
                <select value={paymentData.payment_method} onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })} className={`${SELECT_CLS} w-full`}>
                  <option value="bank_transfer">{t('tickets:paymentModal.method.bankTransfer')}</option>
                  <option value="credit_card">{t('tickets:paymentModal.method.creditCard')}</option>
                  <option value="cash">{t('tickets:paymentModal.method.cash')}</option>
                  <option value="check">{t('tickets:paymentModal.method.check')}</option>
                </select>
              </div>
              <div>
                <label className={LABEL_CLS}>{t('tickets:paymentModal.label.reference')} *</label>
                <input type="text" value={paymentData.transaction_reference} onChange={(e) => setPaymentData({ ...paymentData, transaction_reference: e.target.value })} placeholder={t('tickets:paymentModal.label.referencePlaceholder')} className={INPUT_CLS} required />
              </div>
              <div>
                <label className={LABEL_CLS}>{t('tickets:paymentModal.label.notes')}</label>
                <textarea value={paymentData.notes} onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })} placeholder={t('tickets:paymentModal.label.notesPlaceholder')} className={`${INPUT_CLS} resize-none min-h-[80px]`} rows="3" />
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex gap-3">
                <Icons.Alert size={16} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800"><strong>{t('tickets:paymentModal.important')}</strong> {t('tickets:paymentModal.importantNote')}</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-stone-100 flex justify-end gap-3">
              <Button kind="ghost" onClick={() => setShowPaymentModal(false)}>{t('common:action.cancel')}</Button>
              <Button kind="primary" onClick={handleSubmitPayment} loading={submitPaymentMutation.isPending}>{t('tickets:paymentModal.submit')}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Verification Modal (Managers) */}
      {showVerificationModal && isManager && selectedPayment && (
        <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
            <div className="px-6 py-4 border-b border-stone-100 flex items-center gap-2">
              <Icons.CheckCircle2 size={18} className="text-green-600" />
              <h2 className="text-lg font-bold text-stone-900">{t('tickets:verifyModal.title')}</h2>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className={LABEL_CLS}>{t('tickets:verifyModal.label.details')}</label>
                <div className="bg-stone-50 rounded-xl p-4 border border-stone-200 space-y-2 text-sm">
                  {[
                    { label: t('tickets:verifyModal.kv.amount'),    value: `$${selectedPayment.amount}` },
                    { label: t('tickets:verifyModal.kv.method'),    value: <span className="capitalize">{selectedPayment.payment_method.replace('_', ' ')}</span> },
                    { label: t('tickets:verifyModal.kv.reference'), value: <code className="bg-white px-1.5 py-0.5 rounded border border-stone-200 text-xs">{selectedPayment.transaction_reference}</code> },
                    { label: t('tickets:verifyModal.kv.submitted'), value: new Date(selectedPayment.submitted_at).toLocaleString(locale) },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-stone-500">{label}:</span>
                      <span className="font-medium text-stone-900">{value}</span>
                    </div>
                  ))}
                  {selectedPayment.notes && (
                    <div className="pt-2 border-t border-stone-200">
                      <p className="text-xs text-stone-400 mb-1">{t('tickets:verifyModal.kv.notes')}:</p>
                      <p className="text-sm text-stone-600 italic">"{selectedPayment.notes}"</p>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className={LABEL_CLS}>{t('tickets:verifyModal.label.action')}</label>
                <select value={verificationData.status} onChange={(e) => setVerificationData({ ...verificationData, status: e.target.value })} className={`${SELECT_CLS} w-full`}>
                  <option value="verified">{t('tickets:verifyModal.action.verifyApprove')}</option>
                  <option value="rejected">{t('tickets:verifyModal.action.rejectDecline')}</option>
                </select>
              </div>
              {verificationData.status === 'rejected' && (
                <div>
                  <label className={`${LABEL_CLS} text-red-600`}>{t('tickets:verifyModal.label.rejectionReason')} *</label>
                  <textarea value={verificationData.rejection_reason} onChange={(e) => setVerificationData({ ...verificationData, rejection_reason: e.target.value })} className="w-full px-3 py-2 text-sm border border-red-200 rounded-lg bg-red-50 text-red-900 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 resize-none min-h-[80px]" placeholder={t('tickets:verifyModal.rejectionPlaceholder')} required />
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-stone-100 flex justify-end gap-3">
              <Button kind="ghost" onClick={() => setShowVerificationModal(false)}>{t('common:action.cancel')}</Button>
              <Button
                kind={verificationData.status === 'verified' ? 'success' : 'danger'}
                onClick={handleVerifyPayment}
                loading={verifyPaymentMutation.isPending}
              >
                {verificationData.status === 'verified' ? t('tickets:verifyModal.submit.verify') : t('tickets:verifyModal.submit.reject')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketsUnified;
